import { Table, TablePaginationConfig, TableProps } from 'antd';

/**
 * The default page size for antd table.
 */
export const DEFAULTPAGESIZE = 10;

type PaginatedTableProps<T extends { readonly key: string }> = {
  readonly otherTableProps?: TableProps<T>;
  readonly pagination: {
    readonly page: {
      readonly currentPage: number;
      readonly setCurrentPage: (page: number) => void;
    };
    readonly pageSize: {
      readonly currentPageSize: number;
      readonly setCurrentPageSize: (pageSize: number) => void;
    };
    readonly otherPaginationProps?: TablePaginationConfig;
  };
};

/**
 * Provides a generic paginated table that resets current page when page size changes.
 */
const PaginatedTable = <T extends { readonly key: string }>({
  pagination: {
    page: { currentPage, setCurrentPage },
    pageSize: { currentPageSize, setCurrentPageSize },
    otherPaginationProps,
  },
  otherTableProps,
}: PaginatedTableProps<T>): JSX.Element => (
  <Table
    {...otherTableProps}
    pagination={{
      ...otherPaginationProps,
      current: currentPage,
      defaultPageSize: currentPageSize,
      onChange: (newCurrentPage, newPageSize) => {
        const pageSizeChanged = currentPageSize !== newPageSize;

        // When page size has changed, we reset the current page to 1 and update the current page size.
        if (pageSizeChanged) {
          setCurrentPage(1);
          setCurrentPageSize(newPageSize);
        }
        // When page size has not changed, we update the current page.
        else {
          setCurrentPage(newCurrentPage);
        }
      },
    }}
  />
);

export default PaginatedTable;
